import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, LogOut, Copy, Check, ChevronDown, Smartphone } from "lucide-react";
import { useWallet } from "@/context/WalletContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  discoverEvmProviders, identifyInjectedWallets, identifyRobinhoodWallet,
  fetchEvmPortfolioBalance, fetchSolanaPortfolioBalance,
  switchEvmChain,
} from "@/lib/onchain";
import { WalletIcon } from "@/components/WalletIcon";
import { fmtUSDprecise } from "@/lib/format";
import { isMobile, openWalletDapp } from "@/lib/deeplinks";
import { flipBackerPath, flipProfileChain, getFlipConfig } from "@/lib/flipApi";
import { toast } from "sonner";

const MOBILE_WALLETS = [
  { id: "metamask", label: "MetaMask", eco: "evm" },
  { id: "coinbase", label: "Coinbase Wallet", eco: "evm" },
  { id: "trust", label: "Trust Wallet", eco: "evm" },
  { id: "phantom", label: "Phantom", eco: "solana" },
  { id: "solflare", label: "Solflare", eco: "solana" },
];

function safeProviderIcon(icon) {
  return typeof icon === "string"
    && icon.length <= 100_000
    && /^data:image\/(png|jpeg|webp|svg\+xml);/i.test(icon)
    ? icon
    : null;
}

function providerTestId(name) {
  return String(name || "wallet").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export default function WalletMenu() {
  const { wallet, connectEvmDetail, connectSolanaWallet, connectWalletConnectWC, connectDemo, disconnect } = useWallet();
  const [copied, setCopied] = useState(false);
  const [evmWallets, setEvmWallets] = useState([]);
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(false);
  const [flipEnabled, setFlipEnabled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    getFlipConfig()
      .then((cfg) => setFlipEnabled(Boolean(cfg?.enabled)))
      .catch(() => setFlipEnabled(false));
  }, []);

  const refresh = useCallback(async () => {
    setFlags(identifyInjectedWallets());
    // Phantom's EVM provider stays in the list: it is a legitimate EVM wallet.
    const list = await discoverEvmProviders();
    setEvmWallets(list);
  }, []);

  const onWalletConnect = async () => {
    setLoading(true);
    try { await connectWalletConnectWC(); toast.success("WalletConnect connected"); }
    catch (e) { toast.error(e?.message?.slice(0, 90) || "Connection cancelled"); }
    finally { setLoading(false); }
  };

  const onEvm = async (detail) => {
    setLoading(true);
    try { await connectEvmDetail(detail); toast.success(`${detail.info?.name || "Wallet"} connected`); }
    catch (e) { toast.error(e?.code === 4001 ? "Connection rejected" : (e.message || "Failed")); }
    finally { setLoading(false); }
  };
  const onSolana = async (name) => {
    setLoading(true);
    try { await connectSolanaWallet(name); toast.success(`${name} connected`); }
    catch (e) { toast.error(e?.message || "Failed"); }
    finally { setLoading(false); }
  };

  // Native balance for the connected wallet (ETH on EVM, SOL on Solana).
  const [menuBalance, setMenuBalance] = useState(null);
  useEffect(() => {
    if (!wallet?.real || !wallet?.address) { setMenuBalance(null); return; }
    let cancelled = false;
    const fetchPortfolio = wallet.ecosystem === "solana"
      ? fetchSolanaPortfolioBalance
      : fetchEvmPortfolioBalance;
    fetchPortfolio(wallet.address).then((b) => { if (!cancelled) setMenuBalance(b); })
      .catch(() => { if (!cancelled) setMenuBalance(null); });
    return () => { cancelled = true; };
  }, [wallet?.real, wallet?.address, wallet?.ecosystem]);

  if (wallet) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="wallet-menu-button"
            className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-2 text-sm font-mono transition-colors hover:bg-white/10">
            <span className="h-2 w-2 rounded-full bg-positive animate-pulse-dot" />
            <span className="hidden sm:inline">{wallet.address.slice(0, 6)}...{wallet.address.slice(-4)}</span>
            <ChevronDown className="h-4 w-4 text-zinc-500" strokeWidth={1.5} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 border-white/10 bg-[#0a0a0a]">
          {!wallet.real && <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-gold/80">Demo wallet</div>}
          {wallet.real && menuBalance != null && (
            <div className="px-2 py-1.5 font-mono text-xs text-zinc-300" data-testid="wallet-menu-balance">
              Portfolio: {fmtUSDprecise(menuBalance.usd)}
            </div>
          )}
          {flipEnabled ? (
            <>
              <DropdownMenuItem
                data-testid="wallet-my-profile"
                onClick={() => navigate(flipBackerPath(wallet))}
                className="cursor-pointer font-mono text-xs"
              >
                My Profile
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="wallet-my-calls"
                onClick={() => navigate("/calls?tab=mine")}
                className="cursor-pointer font-mono text-xs"
              >
                My Calls
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="wallet-flip-status"
                onClick={() => navigate(`/backer/${flipProfileChain(wallet)}/${wallet.address}`)}
                className="cursor-pointer font-mono text-xs"
              >
                FLIP Status
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
            </>
          ) : (
            <DropdownMenuItem data-testid="wallet-profile-link" onClick={() => navigate(`/user/${wallet.address}`)} className="cursor-pointer font-mono text-xs">View my profile</DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => { navigator.clipboard.writeText(wallet.address); setCopied(true); setTimeout(() => setCopied(false), 1500); }} className="cursor-pointer font-mono text-xs">
            {copied ? <Check className="mr-2 h-3 w-3" /> : <Copy className="mr-2 h-3 w-3" />}Copy address
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="wallet-disconnect" onClick={disconnect} className="cursor-pointer font-mono text-xs text-negative"><LogOut className="mr-2 h-3 w-3" /> Disconnect</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  const mobile = isMobile();
  const hasInjected = evmWallets.length > 0 || flags.hasSolana;

  return (
    <DropdownMenu onOpenChange={(o) => o && refresh()}>
      <DropdownMenuTrigger asChild>
        <button data-testid="connect-wallet-button"
          className="flex items-center gap-2 rounded-full bg-white px-3.5 py-2 text-sm font-bold text-black transition-transform hover:bg-zinc-200 active:scale-95 sm:px-4">
          <Wallet className="h-4 w-4 shrink-0" strokeWidth={2} />
          <span className="hidden min-[400px]:inline sm:hidden">Connect</span>
          <span className="hidden sm:inline">Connect Wallet</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 border-white/10 bg-[#0a0a0a]" data-testid="wallet-connect-menu">
        {/* WalletConnect: QR pairing with 500+ mobile/hardware wallets */}
        <DropdownMenuItem data-testid="connect-walletconnect" disabled={loading} onClick={onWalletConnect}
          className="cursor-pointer text-sm">
          <WalletIcon name="walletconnect" size={20} className="mr-2" />
          WalletConnect <span className="ml-auto text-[10px] uppercase text-zinc-500">QR · 500+</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-white/10" />

        {/* Robinhood Wallet — first-class when injected */}
        {flags.robinhood && (
          <>
            <div className="px-2 py-1.5 text-[10px] uppercase tracking-[0.2em] text-emerald-500/80">Robinhood</div>
            <DropdownMenuItem
              data-testid="connect-robinhood-wallet"
              disabled={loading}
              onClick={async () => {
                setLoading(true);
                try {
                  const rh = identifyRobinhoodWallet();
                  if (rh.provider) {
                    await onEvm({
                      info: { uuid: "robinhood-wallet", name: "Robinhood Wallet", icon: "", rdns: "com.robinhood.wallet" },
                      provider: rh.provider,
                    });
                    try { await switchEvmChain("robinhood"); } catch { /* user may dismiss add/switch */ }
                  } else {
                    toast.error("Robinhood Wallet not available");
                  }
                } finally {
                  setLoading(false);
                }
              }}
              className="cursor-pointer text-sm"
            >
              <WalletIcon name="Robinhood" size={20} className="mr-2" />
              Robinhood Wallet
              <span className="ml-auto text-[10px] uppercase text-zinc-500">4663</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-white/10" />
          </>
        )}

        {/* Injected EVM wallets (extensions or wallet in-app browsers) */}
        {evmWallets.length > 0 && (
          <>
            <div className="px-2 py-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-500">EVM wallets</div>
            {evmWallets
              .filter((d) => !(flags.robinhood && /robinhood/i.test(d.info?.name || "")))
              .map((d) => {
              const icon = safeProviderIcon(d.info.icon);
              return (
                <DropdownMenuItem key={d.info.uuid} data-testid={`connect-detected-${providerTestId(d.info.name)}`}
                  disabled={loading} onClick={() => onEvm(d)} className="cursor-pointer text-sm">
                  {icon
                    ? <img src={icon} alt="" className="mr-2 h-5 w-5 rounded" />
                    : <WalletIcon name={d.info.name} size={20} className="mr-2" />}
                  {d.info.name || "Browser wallet"}
                </DropdownMenuItem>
              );
            })}
          </>
        )}
        {/* Injected Solana */}
        {(flags.phantom || flags.solflare) && (
          <div className="px-2 py-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-500">Solana wallets</div>
        )}
        {flags.phantom && (
          <DropdownMenuItem data-testid="connect-phantom" disabled={loading} onClick={() => onSolana("Phantom")} className="cursor-pointer text-sm">
            <WalletIcon name="Phantom" size={20} className="mr-2" /> Phantom <span className="ml-auto text-[10px] uppercase text-zinc-500">Solana</span>
          </DropdownMenuItem>
        )}
        {flags.solflare && (
          <DropdownMenuItem data-testid="connect-solflare" disabled={loading} onClick={() => onSolana("Solflare")} className="cursor-pointer text-sm">
            <WalletIcon name="Solflare" size={20} className="mr-2" /> Solflare <span className="ml-auto text-[10px] uppercase text-zinc-500">Solana</span>
          </DropdownMenuItem>
        )}

        {/* Mobile: no injected provider → deep-link into wallet in-app browser */}
        {mobile && !hasInjected && (
          <>
            <div className="flex items-center gap-1.5 px-2 py-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              <Smartphone className="h-3 w-3" /> Open in a wallet app
            </div>
            {MOBILE_WALLETS.map((w) => (
              <DropdownMenuItem key={w.id} data-testid={`deeplink-${w.id}`}
                onClick={() => openWalletDapp(w.id)} className="cursor-pointer text-sm">
                <WalletIcon name={w.label} size={20} className="mr-2" />
                Open in {w.label}
              </DropdownMenuItem>
            ))}
            <p className="px-2 py-1 text-[10px] leading-relaxed text-zinc-600">
              This opens FLIP inside the wallet's browser, where it can connect.
            </p>
          </>
        )}

        {/* Desktop with no extension */}
        {!mobile && evmWallets.length === 0 && !flags.hasSolana && (
          <div className="px-2 py-2 text-xs text-zinc-500">No wallet extension found. Install MetaMask or Phantom, then reload.</div>
        )}

        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem data-testid="connect-demo" onClick={() => { connectDemo("evm"); toast.success("Demo wallet connected"); }} className="cursor-pointer text-xs text-zinc-400">
          Continue in demo mode
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
